import { useCallback, useContext, useState, ChangeEvent } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
} from '@mui/material';

import { ModalContext } from '../../../contexts/ModalContext';
import { MODAL_EXTEND_TRIAL } from '../constants';
import { extendTrial } from '../../../domains/trials/api/extendTrial';
import styles from './TrialExtendModal.module.css';

const trialExtendPeriod = [15, 30, 60];

const TrialExtendModal = () => {
  const [selectedPeriod, setSelectedPeriod] = useState(trialExtendPeriod[0]);
  const { isModalOpen, closeModal, openModalProps } =
    useContext(ModalContext) || {};

  const isOpen = isModalOpen?.(MODAL_EXTEND_TRIAL);

  const { modalProps, onSuccess } =
    (openModalProps?.[MODAL_EXTEND_TRIAL] as {
      modalProps: Record<string, string>;
      onSuccess: () => void;
    }) || {};

  const handleCloseModal = useCallback(() => {
    closeModal?.(MODAL_EXTEND_TRIAL);
  }, [closeModal]);

  const onBundleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedPeriod(Number(event.target.value));
  };

  const handleExtendTrial = async () => {
    if (!modalProps.bundleId) {
      return;
    }

    const errorMessage = `Unable to extend trial for site_id: ${modalProps?.siteId}`;

    await extendTrial({
      bundleId: modalProps.bundleId,
      duration: selectedPeriod,
      errorMessage,
      accountId: Number(modalProps?.accountId),
    });

    onSuccess();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={isOpen ?? false}
      onClose={handleCloseModal}
    >
      <DialogTitle>Extend Trial</DialogTitle>
      <DialogContent>
        <p className={styles.copy}>
          You're about to extend a trial for site{' '}
          {modalProps?.name ? modalProps.name : `ID: ${modalProps?.siteId}`}
        </p>
        <p className={styles.bold}>Extend trial by:</p>
        <div className={styles.options}>
          {trialExtendPeriod.map((period) => {
            return (
              <div className={styles.option}>
                <Radio
                  onChange={onBundleChange}
                  id={`${period}-days`}
                  name={`${period} days`}
                  value={period}
                  checked={selectedPeriod === period}
                />
                <label htmlFor={`${period}-days`}>{`${period} days`}</label>
              </div>
            );
          })}
        </div>
        <p>
          The current trial will be extended by the number of days you chose
          above.
        </p>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseModal}>Cancel</Button>
        <Button variant="contained" onClick={handleExtendTrial}>
          Extend trial
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrialExtendModal;
